import React, { useState, useEffect, useCallback, useRef, useTransition } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import HistoryIcon from '@mui/icons-material/History';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import LockIcon from '@mui/icons-material/Lock';
import * as accessoryPriceApi from '../api/accessoryPriceApi';
import * as accessoryApi from '../api/accessoryApi';
import * as accessoryGroupApi from '../api/accessoryGroupApi';
import * as departmentApi from '../api/departmentApi';
import * as makeApi from '../api/makeApi';
import * as priceChangeBasisApi from '../api/priceChangeBasisApi';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';
import { getCached } from '../utils/apiCache';

const fmt = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function AccessoryPriceManagement() {
  const [departments, setDepartments] = useState([]);
  const [accessoryGroups, setAccessoryGroups] = useState([]);
  const [accessoryOptions, setAccessoryOptions] = useState([]);
  const [makes, setMakes] = useState([]);
  const [basisOptions, setBasisOptions] = useState([]);

  const [filterDept, setFilterDept] = useState(null);
  const [filterGroup, setFilterGroup] = useState(null);
  const [filterAccessory, setFilterAccessory] = useState(null);
  const [filterMake, setFilterMake] = useState(null);
  // accessorySearch stored in a ref — updating it does NOT trigger a re-render.
  // Only the debounced API call reads it, so typing stays instant.
  const accessorySearchRef = useRef('');
  const searchTimer = useRef(null);
  // Increment to force-remount the Autocomplete (clears displayed input on "Clear All")
  const [accessoryInputKey, setAccessoryInputKey] = useState(0);

  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ accessoryId: '', makeId: '', price: '', basisId: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editHistoryEntry, setEditHistoryEntry] = useState(null);
  const [editHistorySaving, setEditHistorySaving] = useState(false);

  // startTransition marks setAccessoryOptions as a non-urgent "transition" update.
  // React 18 will prioritise new keystrokes over these re-renders, so typing in
  // the Accessory Autocomplete never causes the price table to block the input.
  const [, startTransition] = useTransition();

  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canEdit   = hasPermission('EDIT_PRICES');
  const canDelete = hasPermission('EDIT_PRICES') || hasPermission('DELETE_PRICES');

  useEffect(() => {
    Promise.all([
      getCached('departments:all', () => departmentApi.getAll()),
      getCached('makes:all', () => makeApi.getAll()),
      getCached('accessoryGroups:all', () => accessoryGroupApi.getAll()),
      getCached('priceChangeBasis:active', () => priceChangeBasisApi.getAll({ activeOnly: 'true' })),
    ])
      .then(([d, m, g, b]) => {
        setDepartments(Array.isArray(d) ? d : (d.data || []));
        setMakes(Array.isArray(m) ? m : (m.data || []));
        setAccessoryGroups(Array.isArray(g) ? g : (g.data || []));
        setBasisOptions(Array.isArray(b) ? b : (b.data || []));
      })
      .catch(console.error);
  }, []);

  const handleGroupSelect = (group) => {
    setFilterGroup(group);
    setFilterAccessory(null);
    if (group && !filterDept) {
      const deptObj = (departments).find((d) => d.id === group.departmentId);
      if (deptObj) setFilterDept(deptObj);
    }
  };

  const handleAccessorySelect = (accessory) => {
    setFilterAccessory(accessory);
    if (!accessory) return;
    const accGroup = accessory.accessoryGroup;
    const accDept = accGroup?.department;
    if (!filterDept && accDept) { setFilterDept(accDept); if (accGroup) setFilterGroup(accGroup); }
    else if (filterDept && !filterGroup && accGroup) setFilterGroup(accGroup);
  };

  // Re-fetch accessory options whenever the dept/group filter changes.
  // Typing is handled directly in onInputChange (no state update = no render).
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const params = { limit: 50 };
        if (accessorySearchRef.current) params.search = accessorySearchRef.current;
        if (filterGroup) params.accessoryGroupId = filterGroup.id;
        else if (filterDept) params.departmentId = filterDept.id;
        const res = await accessoryApi.getAll(params);
        // Transition: updating the dropdown list is non-urgent.
        // React will yield to any pending keystrokes before committing this render.
        startTransition(() => setAccessoryOptions(res.data || res));
      } catch (e) { console.error(e); }
    }, 0);
    return () => clearTimeout(searchTimer.current);
  }, [filterGroup, filterDept, startTransition]);

  const loadPrices = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = {};
      if (filterAccessory) params.accessoryId = filterAccessory.id;
      if (filterMake) params.makeId = filterMake.id;
      const res = await accessoryPriceApi.getAll(params);
      setPrices(res.data || res);
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [filterAccessory, filterMake, showSnackbar]);

  useEffect(() => { loadPrices(); }, [loadPrices]);

  const clearFilters = () => {
    setFilterDept(null);
    setFilterGroup(null);
    setFilterAccessory(null);
    setFilterMake(null);
    accessorySearchRef.current = '';
    // Force-remount the Autocomplete so its displayed input text is cleared
    setAccessoryInputKey((k) => k + 1);
  };

  // ── Selection helpers ─────────────────────────────────────────────────────
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === prices.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(prices.map((p) => p.id)));
  };
  const allSelected   = prices.length > 0 && selectedIds.size === prices.length;
  const someSelected  = selectedIds.size > 0 && selectedIds.size < prices.length;

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openCreate = () => { setEditItem(null); setForm({ accessoryId: '', makeId: '', price: '', basisId: '', remarks: '' }); setDialogOpen(true); };
  const openEdit = (row) => { setEditItem(row); setForm({ accessoryId: row.accessoryId, makeId: row.makeId, price: String(row.price), basisId: '', remarks: '' }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!editItem && !form.basisId) { showSnackbar('Please select a basis for the price change', 'error'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await accessoryPriceApi.update(editItem.id, { price: Number(form.price), basisId: form.basisId ? Number(form.basisId) : null, remarks: form.remarks });
        showSnackbar('Price updated. History recorded.', 'success');
      } else {
        await accessoryPriceApi.create({ accessoryId: Number(form.accessoryId), makeId: Number(form.makeId), price: Number(form.price), basisId: Number(form.basisId), remarks: form.remarks });
        showSnackbar('Price added. History recorded.', 'success');
      }
      setDialogOpen(false); loadPrices();
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await accessoryPriceApi.remove(deleteTarget.id); showSnackbar('Latest price removed.', 'info'); setDeleteTarget(null); loadPrices(); }
    catch (e) { showSnackbar(e.message, 'error'); setDeleteTarget(null); }
  };

  const handleBulkDelete = async () => {
    setBulkDeleteOpen(false);
    try {
      await accessoryPriceApi.batchRemove([...selectedIds]);
      showSnackbar(`${selectedIds.size} price record(s) removed. History preserved.`, 'info');
      loadPrices();
    } catch (e) { showSnackbar(e.message, 'error'); }
  };

  const openHistory = async (row) => {
    setHistoryItem(row); setHistoryLoading(true);
    try { const res = await accessoryPriceApi.getHistory({ accessoryId: row.accessoryId, makeId: row.makeId }); setHistory(res.data || res); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setHistoryLoading(false); }
  };

  const openEditHistoryEntry = (h) => {
    setEditHistoryEntry({ id: h.id, basisId: h.basis?.id || '', remarks: h.remarks || '' });
  };

  const handleSaveHistoryBasis = async () => {
    setEditHistorySaving(true);
    try {
      await accessoryPriceApi.updateHistoryBasis(editHistoryEntry.id, {
        basisId: editHistoryEntry.basisId ? Number(editHistoryEntry.basisId) : null,
        remarks: editHistoryEntry.remarks || null,
      });
      showSnackbar('Price history updated.', 'success');
      setEditHistoryEntry(null);
      if (historyItem) {
        const res = await accessoryPriceApi.getHistory({ accessoryId: historyItem.accessoryId, makeId: historyItem.makeId });
        setHistory(res.data || res);
      }
    } catch (e) { showSnackbar(e.message, 'error'); }
    finally { setEditHistorySaving(false); }
  };

  const hasFilters = filterDept || filterGroup || filterAccessory || filterMake;
  const filteredGroups = filterDept ? (accessoryGroups).filter((g) => g.departmentId === filterDept.id) : accessoryGroups;

  const existingCombo = !editItem && form.accessoryId && form.makeId
    ? prices.find((p) => p.accessoryId === Number(form.accessoryId) && p.makeId === Number(form.makeId))
    : null;

  return (
    <Box>
      <PageHeader
        title="Accessory Price Management"
        subtitle="Latest prices per accessory + make. Every change is permanently logged."
        action={canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Price</Button>}
      />
      {!canEdit && !canDelete && <Alert severity="info" sx={{ mb: 2 }}>You have read-only access to prices.</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <FilterListIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>Filters</Typography>
            {hasFilters && <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ ml: 'auto' }}>Clear All</Button>}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={departments} getOptionLabel={(o) => o.name}
                value={filterDept} onChange={(_, v) => { setFilterDept(v); setFilterGroup(null); setFilterAccessory(null); }}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Department" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={filteredGroups} getOptionLabel={(o) => o.groupCode}
                value={filterGroup} onChange={(_, v) => handleGroupSelect(v)}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Group Code" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{o.groupCode}</Typography>
                      {!filterDept && <Typography variant="caption" color="text.secondary">{(departments).find((d) => d.id === o.departmentId)?.name || ''}</Typography>}
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {/* key forces remount (clears input) when "Clear All" is clicked.           */}
              {/* onInputChange writes to a ref — no state update, no parent re-render.   */}
              <Autocomplete
                key={accessoryInputKey}
                size="small"
                options={accessoryOptions}
                getOptionLabel={(o) => o.name}
                value={filterAccessory}
                onChange={(_, v) => handleAccessorySelect(v)}
                onInputChange={(_, v) => {
                  accessorySearchRef.current = v;
                  clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(async () => {
                    try {
                      const params = { limit: 50 };
                      if (v) params.search = v;
                      if (filterGroup) params.accessoryGroupId = filterGroup.id;
                      else if (filterDept) params.departmentId = filterDept.id;
                      const res = await accessoryApi.getAll(params);
                      startTransition(() => setAccessoryOptions(res.data || res));
                    } catch (e) { console.error(e); }
                  }, 300);
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Accessory" helperText="Type to search" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box><Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.unit} · {o.accessoryGroup?.groupCode}</Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete size="small" options={makes} getOptionLabel={(o) => o.name}
                value={filterMake} onChange={(_, v) => setFilterMake(v)} isOptionEqualToValue={(o, v) => o.id === v?.id}
                renderInput={(params) => <TextField {...params} label="Make" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk action toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#FFF3E0', borderRadius: 2, border: '1px solid #FFB300' }}>
          <Typography variant="body2" fontWeight={600}>{selectedIds.size} row(s) selected</Typography>
          <Button
            variant="contained" color="error" size="small"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setBulkDeleteOpen(true)}
          >
            Delete Selected
          </Button>
          <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ ml: 'auto' }}>
            Clear Selection
          </Button>
        </Box>
      )}

      <Card>
        {loading ? <LoadingSpinner /> : (
          <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                  {canDelete && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>#</TableCell>
                  <TableCell>Accessory</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Make</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prices.map((p, i) => (
                  <TableRow
                    key={p.id}
                    hover
                    selected={selectedIds.has(p.id)}
                    sx={{ bgcolor: selectedIds.has(p.id) ? '#FFF8E1 !important' : undefined }}
                  >
                    {canDelete && (
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selectedIds.has(p.id)} onChange={() => toggleRow(p.id)} />
                      </TableCell>
                    )}
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{p.accessory?.name}</TableCell>
                    <TableCell><Chip label={p.accessory?.unit} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ color: '#1565C0' }}>{p.accessory?.accessoryGroup?.groupCode}</TableCell>
                    <TableCell>{p.accessory?.accessoryGroup?.department?.name}</TableCell>
                    <TableCell>{p.make?.name}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2E7D32' }}>{fmt(p.price)}</TableCell>
                    <TableCell sx={{ color: '#757575', fontSize: 12 }}>{new Date(p.updatedAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Price History">
                        <IconButton size="small" color="primary" onClick={() => openHistory(p)}><HistoryIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {canEdit && (
                        <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete this price">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(p)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {prices.length === 0 && (
                  <TableRow><TableCell colSpan={canDelete ? 10 : 9} align="center" sx={{ py: 4, color: '#9e9e9e' }}>
                    {hasFilters ? 'No prices found for selected filters' : 'No prices found'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
        {prices.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">{prices.length} record(s) shown</Typography>
            {canDelete && prices.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Use checkboxes to multi-select for bulk delete'}
              </Typography>
            )}
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem ? 'Update Price' : 'Add Price'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {!editItem && (
            <>
              <FormControl fullWidth>
                <InputLabel>Accessory</InputLabel>
                <Select value={form.accessoryId} label="Accessory" onChange={(e) => setForm({ ...form, accessoryId: e.target.value })}>
                  {accessoryOptions.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Make</InputLabel>
                <Select value={form.makeId} label="Make" onChange={(e) => setForm({ ...form, makeId: e.target.value })}>
                  {makes.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}
          {editItem && (
            <Box sx={{ p: 1.5, bgcolor: '#F5F5F5', borderRadius: 1 }}>
              <Typography variant="body2"><strong>{editItem.accessory?.name}</strong> / {editItem.make?.name}</Typography>
              <Typography variant="caption" color="text.secondary">Previous price: {fmt(editItem.price)}</Typography>
            </Box>
          )}
          {existingCombo && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              This accessory–make combination already exists with a price of{' '}
              <strong>{fmt(existingCombo.price)}</strong>. Saving will <strong>update</strong> the existing record.
            </Alert>
          )}
          <TextField label="Price (₹)" type="number" fullWidth value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <FormControl fullWidth required={!editItem}>
            <InputLabel>{editItem ? 'Basis for Change (optional)' : 'Basis for Change *'}</InputLabel>
            <Select
              value={form.basisId}
              label={editItem ? 'Basis for Change (optional)' : 'Basis for Change *'}
              onChange={(e) => setForm({ ...form, basisId: e.target.value })}
            >
              {editItem && <MenuItem value=""><em>— None / Not specified —</em></MenuItem>}
              {basisOptions.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Remarks (optional)" fullWidth multiline rows={2} value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            placeholder="Any additional notes about this price change..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!form.price || (!editItem && !form.basisId) || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyItem} onClose={() => setHistoryItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>Price History — {historyItem?.accessory?.name} / {historyItem?.make?.name}</DialogTitle>
        <DialogContent>
          {historyLoading ? <LoadingSpinner /> : (
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell><TableCell>Price</TableCell><TableCell>Basis</TableCell>
                  <TableCell>Updated By</TableCell><TableCell>Remarks</TableCell><TableCell>Recorded At</TableCell>
                  {canEdit && <TableCell sx={{ width: 44 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={h.id} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2E7D32' }}>{fmt(h.price)}</TableCell>
                    <TableCell>
                      {h.basis
                        ? <Chip label={h.basis.name} size="small" color="info" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      {h.user ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LockIcon sx={{ fontSize: 12, color: '#757575' }} />
                          <Typography variant="body2">{h.user.username}</Typography>
                        </Box>
                      ) : <Typography variant="caption" color="text.disabled">System</Typography>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}><Typography variant="caption">{h.remarks || '—'}</Typography></TableCell>
                    <TableCell>{new Date(h.recordedAt).toLocaleString('en-IN')}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <Tooltip title={h.basis ? 'Edit basis / remarks' : 'Set missing basis'}>
                          <IconButton size="small" color={h.basis ? 'default' : 'warning'} onClick={() => openEditHistoryEntry(h)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {history.length === 0 && <TableRow><TableCell colSpan={canEdit ? 7 : 6} align="center">No history</TableCell></TableRow>}
              </TableBody>
            </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setHistoryItem(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Edit price history entry (basis / remarks) */}
      <Dialog open={!!editHistoryEntry} onClose={() => setEditHistoryEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Price History Entry</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Basis of Price Change</InputLabel>
            <Select
              value={editHistoryEntry?.basisId || ''}
              label="Basis of Price Change"
              onChange={(e) => setEditHistoryEntry((prev) => ({ ...prev, basisId: e.target.value }))}
            >
              <MenuItem value=""><em>— None / Not specified —</em></MenuItem>
              {basisOptions.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="Remarks"
            fullWidth
            multiline
            rows={2}
            value={editHistoryEntry?.remarks || ''}
            onChange={(e) => setEditHistoryEntry((prev) => ({ ...prev, remarks: e.target.value }))}
            placeholder="Optional notes about this price change..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditHistoryEntry(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHistoryBasis} disabled={editHistorySaving}>
            {editHistorySaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Remove Latest Price"
        message={`Remove the price for "${deleteTarget?.accessory?.name} / ${deleteTarget?.make?.name}"? Price history will be preserved.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title={`Delete ${selectedIds.size} Price Record(s)`}
        message={`Permanently remove ${selectedIds.size} selected price record(s)? Price history for each will be preserved. This cannot be undone.`}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
        confirmColor="error"
      />
    </Box>
  );
}
